import React, { useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  Progress,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import {
  importGlucoseCsv,
  importStressFiles,
  importZipFile,
} from "api/visaVitaService";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // Backend upload limit

const IMPORT_TYPES = {
  corsano: {
    label: "Corsano ZIP",
    description:
      "A ZIP with Corsano CSV exports (activity, emography, HRV, temperature, sleep, blood pressure, stress). " +
      "Each folder in the ZIP is imported as one patient, unless you give a patient identifier.",
    accept: ".zip",
    multiple: false,
  },
  glucose: {
    label: "Glucose CSV",
    description:
      "A glucose CSV file. The patient ID is read from the second column; new patients are created automatically.",
    accept: ".csv",
    multiple: false,
  },
  stress: {
    label: "Stress files",
    description:
      "One ZIP, or one or more CSV files named <patientId>_<type>.csv " +
      "(rr_interval, ppg2, ppg3, acc, bioz, heat_flux, annotation, selfreport).",
    accept: ".zip,.csv",
    multiple: true,
  },
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFiles = (type, files) => {
  if (files.length === 0) return "Select a file to import";

  const allowed = IMPORT_TYPES[type].accept.split(",");
  const wrongType = files.find(
    (file) => !allowed.some((ext) => file.name.toLowerCase().endsWith(ext))
  );
  if (wrongType) return `${wrongType.name} is not a ${allowed.join(" or ")} file`;

  const empty = files.find((file) => file.size === 0);
  if (empty) return `${empty.name} is empty`;

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > MAX_FILE_SIZE) return `Upload is too large (${formatSize(totalSize)}, max 100 MB)`;

  if (type === "stress" && files.length > 1 && files.some((file) => file.name.toLowerCase().endsWith(".zip"))) {
    return "Upload either one ZIP file or CSV files, not both";
  }
  return null;
};

// Glucose import answers with a summary; the ZIP and stress imports answer with a list of persons.
const toPatientRows = (type, result) => {
  if (type === "glucose") {
    return (result.ptIds || []).map((ptId) => ({
      identifier: `${result.prefix}_${ptId}`,
      label: ptId,
    }));
  }
  return (result.importedPersons || []).map((item) => ({
    identifier: item.identifier,
    label: item.identifier,
    personId: item.person?.id,
  }));
};

const ImportData = () => {
  const [importType, setImportType] = useState("corsano");
  const [files, setFiles] = useState([]);
  const [identifier, setIdentifier] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const fileInputRef = useRef(null);
  const history = useHistory();
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const dropBg = useColorModeValue("gray.50", "gray.700");

  const config = IMPORT_TYPES[importType];

  const resetSelection = () => {
    setFiles([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTypeChange = (value) => {
    setImportType(value);
    setIdentifier("");
    setResult(null);
    resetSelection();
  };

  const selectFiles = (selected) => {
    const list = Array.from(selected || []);
    setFiles(config.multiple ? list : list.slice(0, 1));
    setError(null);
    setResult(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (!isImporting) selectFiles(event.dataTransfer.files);
  };

  const handleImport = async () => {
    const validationError = validateFiles(importType, files);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsImporting(true);
    setError(null);
    setResult(null);

    try {
      let response;
      if (importType === "corsano") {
        response = await importZipFile(files[0], identifier.trim() || undefined);
      } else if (importType === "glucose") {
        response = await importGlucoseCsv(files[0]);
      } else {
        response = await importStressFiles(files);
      }

      if (response?.success === false) {
        throw new Error(response.message || "Import failed");
      }

      setResult({ type: importType, data: response, patients: toPatientRows(importType, response) });
      resetSelection();
      toast({
        title: "Import finished",
        description: response?.message,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      setError(err.message || "Import failed");
    } finally {
      setIsImporting(false);
    }
  };

  const renderStats = () => {
    const { type, data } = result;
    const stats =
      type === "glucose"
        ? [
            { label: "Patients in file", value: data.personsInFile },
            { label: "New patients", value: data.personsCreated },
            { label: "Records imported", value: data.insertedRecords },
            { label: "Rows skipped", value: data.skippedRows },
          ]
        : [{ label: "Patients imported", value: data.importedPersonsCount }];

    return (
      <SimpleGrid columns={{ base: 2, md: stats.length }} spacing={4}>
        {stats.map((stat) => (
          <Stat key={stat.label} p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md">
            <StatLabel>{stat.label}</StatLabel>
            <StatNumber>{stat.value ?? 0}</StatNumber>
          </Stat>
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Container maxW="full" py={8} bg={bgColor} minH="100vh">
      <VStack spacing={6} align="stretch">
        <Box mt={10}>
          <Heading size="lg">Import Data</Heading>
          <Text color="gray.500" mt={1}>
            Upload patient measurement files to the VisaVita backend.
          </Text>
        </Box>

        <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
          <VStack spacing={6} align="stretch">
            <FormControl>
              <FormLabel>Data type</FormLabel>
              <RadioGroup value={importType} onChange={handleTypeChange} isDisabled={isImporting}>
                <Stack direction={{ base: "column", md: "row" }} spacing={6}>
                  {Object.entries(IMPORT_TYPES).map(([key, type]) => (
                    <Radio key={key} value={key} colorScheme="purple">
                      {type.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
              <FormHelperText>{config.description}</FormHelperText>
            </FormControl>

            {importType === "corsano" && (
              <FormControl>
                <FormLabel>Patient identifier (optional)</FormLabel>
                <Input
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  placeholder="Leave empty to use the folder names in the ZIP"
                  isDisabled={isImporting}
                />
              </FormControl>
            )}

            <Box
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={borderColor}
              borderRadius="lg"
              bg={dropBg}
              p={8}
              textAlign="center"
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={config.accept}
                multiple={config.multiple}
                style={{ display: "none" }}
                onChange={(event) => selectFiles(event.target.files)}
              />
              <Text mb={3}>
                Drag {config.multiple ? "files" : "a file"} here, or
              </Text>
              <Button
                variant="outline"
                colorScheme="purple"
                onClick={() => fileInputRef.current?.click()}
                isDisabled={isImporting}
              >
                Choose {config.multiple ? "files" : "file"}
              </Button>
              <Text fontSize="sm" color="gray.500" mt={3}>
                Accepted: {config.accept} · max 100 MB
              </Text>

              {files.length > 0 && (
                <VStack mt={4} spacing={1}>
                  {files.map((file) => (
                    <HStack key={file.name}>
                      <Text fontWeight="semibold">{file.name}</Text>
                      <Badge>{formatSize(file.size)}</Badge>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>

            {isImporting && (
              <Box>
                <Text mb={2}>Uploading and importing. Large files can take a few minutes…</Text>
                <Progress size="sm" isIndeterminate colorScheme="purple" />
              </Box>
            )}

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <HStack justify="flex-end">
              <Button onClick={resetSelection} isDisabled={isImporting || files.length === 0}>
                Clear
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleImport}
                isLoading={isImporting}
                loadingText="Importing"
                isDisabled={files.length === 0}
              >
                Import
              </Button>
            </HStack>
          </VStack>
        </Box>

        {result && (
          <Box bg={cardBg} shadow="md" borderRadius="lg" p={6}>
            <VStack spacing={5} align="stretch">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {result.data.message || "Import finished"} ({IMPORT_TYPES[result.type].label})
                </AlertDescription>
              </Alert>

              {renderStats()}

              {result.patients.length > 0 && (
                <TableContainer>
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Patient</Th>
                        <Th>Person ID</Th>
                        <Th textAlign="right">Details</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {result.patients.map((patient) => (
                        <Tr key={patient.identifier}>
                          <Td>{patient.label}</Td>
                          <Td>{patient.personId ?? "—"}</Td>
                          <Td textAlign="right">
                            <Button
                              size="xs"
                              variant="link"
                              colorScheme="purple"
                              onClick={() =>
                                history.push(`/admin/patient-details/${encodeURIComponent(patient.identifier)}`)
                              }
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </VStack>
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default ImportData;
